import React, { useState } from 'react';
import { useAuth } from '../AuthProvider';
import AuthShell, { Label, Input, PrimaryButton, ErrorBanner } from '../AuthShell';

const MIN_LEN = 12;

function passwordPolicyError(pw) {
  if (pw.length < MIN_LEN) return `Password must be at least ${MIN_LEN} characters.`;
  if (!/[A-Z]/.test(pw)) return 'Password must contain an uppercase letter.';
  if (!/[a-z]/.test(pw)) return 'Password must contain a lowercase letter.';
  if (!/[0-9]/.test(pw)) return 'Password must contain a digit.';
  if (!/[^A-Za-z0-9]/.test(pw)) return 'Password must contain a symbol.';
  return null;
}

export default function PasswordResetPage() {
  const { completePasswordReset, user } = useAuth();
  const [pw1, setPw1] = useState('');
  const [pw2, setPw2] = useState('');
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (pw1 !== pw2) { setError('New passwords do not match.'); return; }
    const policy = passwordPolicyError(pw1);
    if (policy) { setError(policy); return; }
    setLoading(true);
    try {
      await completePasswordReset(pw1);
      // AuthRouter advances to MfaEnrollPage automatically.
    } catch (err) {
      setError(
        err?.detail === 'password_too_weak' ? 'Password does not meet policy.' :
        err?.detail === 'user_disabled' ? 'Account is disabled. Contact an administrator.' :
        err?.message || 'Could not reset password.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Set a new password"
      subtitle={user?.email
        ? `Signed in as ${user.email}. Your temporary password must be replaced before you can continue.`
        : 'Your temporary password must be replaced before you can continue.'}
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <ErrorBanner>{error}</ErrorBanner>
        <div>
          <Label htmlFor="pw1">New password</Label>
          <Input id="pw1" type="password" autoComplete="new-password" required autoFocus
                 value={pw1} onChange={(e) => setPw1(e.target.value)} />
          <p className="mt-1 text-xs text-surface-muted dark:text-surface-dark-muted">
            At least {MIN_LEN} characters, with upper + lower + digit + symbol.
          </p>
        </div>
        <div>
          <Label htmlFor="pw2">Confirm new password</Label>
          <Input id="pw2" type="password" autoComplete="new-password" required
                 value={pw2} onChange={(e) => setPw2(e.target.value)} />
        </div>
        <PrimaryButton type="submit" loading={loading}>Update password</PrimaryButton>
      </form>
    </AuthShell>
  );
}
